import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import {
    EngineTagEventPersonalService,
    EngineTagService,
    ObjectQueryFilter,
    TagDTO,
    UpdateTagByDataviewDTO
} from '@tj/tj-dmp-lib-api';
import { TagJobService, TagJobStatus } from '../../../../services/tag-job.service';

/** Règle de tag sur un preset, au lieu d'un tag ponctuel des résultats. */
export interface TagRuleTarget {
    presetId: string;
    presetName: string;
    /** Filtres de l'écran modifiés depuis le chargement du preset : la règle est refusée. */
    presetModified: boolean;
}

export interface TagResultsDialogData {
    tenant: string;
    partner: string;
    dataviewId: string;
    queryFilter: ObjectQueryFilter;
    /** Arbre de filtres de l'écran, gardé avec l'événement pour l'historique des tags. */
    queryFilterUi: string;
    /** Nombre de lignes de la recherche : des contacts, ou des commandes. */
    totalRecords: number;
    /** Vue de contacts : une ligne est un contact, le nombre de contacts est connu d'avance. */
    personalView: boolean;
    rule?: TagRuleTarget;
    initialStatus?: boolean;
    initialTagIds?: number[];
}

export interface TagResultsDialogResult {
    status: boolean;
    tagIds: number[];
    tagNames: string[];
    personalCount: number;
    /**
     * Application lancée en tâche de fond par l'engine : la page la suit jusqu'à sa fin pour annoncer le
     * résultat réel. Déjà terminée, sans changement, pour une règle enregistrée sans application.
     */
    job: TagJobStatus;
    /** Règle enregistrée (et non tag ponctuel). */
    rule: boolean;
    applied: boolean;
    scheduled: boolean;
}

/**
 * Ajoute ou retire des tags sur les contacts d'une recherche, une fois (bouton « Taguer ») ou par une
 * règle sur le preset chargé (« Automatiser »). Le nombre de contacts concernés est affiché avant de
 * valider : sur une vue de commandes, ce sont les clients qui reçoivent le tag, et plusieurs commandes
 * d'un même client comptent pour un.
 *
 * La modale se ferme dès que l'application est lancée : sur ~100 000 contacts elle dure, et se poursuit
 * en tâche de fond pendant que l'utilisateur continue à travailler.
 */
@Component({
    selector: 'dmp-tag-results-dialog',
    templateUrl: './tag-results-dialog.component.html',
    styleUrls: ['./tag-results-dialog.component.scss'],
    standalone: false
})
export class TagResultsDialogComponent implements OnInit {
    data: TagResultsDialogData;

    status = true;
    statusOptions: { label: string; value: boolean; icon: string }[] = [];
    tagsOptions: TagDTO[] = [];
    selectedTagIds: number[] = [];

    /** Règle : programmée (cron) ou à la demande. */
    scheduled = false;
    scheduleOptions: { label: string; value: boolean }[] = [];
    cron: string = null;
    applyNow = false;

    /** `undefined` pendant le calcul, `null` si le calcul a échoué. */
    personalCount: number | null | undefined = undefined;
    applying = false;

    constructor(
        private ref: DynamicDialogRef,
        config: DynamicDialogConfig,
        private engineTagService: EngineTagService,
        private engineTagEventPersonalService: EngineTagEventPersonalService,
        private tagJobService: TagJobService,
        private translate: TranslateService
    ) {
        this.data = config.data;
    }

    get isRule(): boolean {
        return !!this.data.rule;
    }

    /** Règle refusée : elle appliquerait les filtres enregistrés, pas ceux affichés. */
    get blocked(): boolean {
        return !!this.data.rule?.presetModified;
    }

    ngOnInit(): void {
        this.status = this.data.initialStatus ?? true;
        this.selectedTagIds = [...(this.data.initialTagIds ?? [])];
        this.statusOptions = [
            { label: this.translate.instant('tagResults.add'), value: true, icon: 'fas fa-plus' },
            { label: this.translate.instant('tagResults.remove'), value: false, icon: 'fas fa-minus' }
        ];
        this.scheduleOptions = [
            { label: this.translate.instant('tagResults.rule.onDemand'), value: false },
            { label: this.translate.instant('tagResults.rule.scheduled'), value: true }
        ];
        if (this.blocked) {
            return;
        }
        this.engineTagService.getAllTags(this.data.tenant, true, 0, 1000).subscribe(tags => {
            this.tagsOptions = tags;
        });
        if (this.data.personalView) {
            this.personalCount = this.data.totalRecords;
        } else {
            this.engineTagEventPersonalService
                .countPersonalsForTagByDataview(this.buildBody(), this.data.tenant, this.data.partner)
                .subscribe({
                    next: count => this.personalCount = count,
                    error: () => this.personalCount = null
                });
        }
    }

    onScheduledChange(): void {
        // Valeur de départ : tous les jours à l'heure actuelle, arrondie aux 5 minutes inférieures.
        if (this.scheduled && !this.cron) {
            const now = new Date();
            this.cron = `${Math.floor(now.getMinutes() / 5) * 5} ${now.getHours()} * * *`;
        }
    }

    /** Le tag s'applique maintenant : tag ponctuel, ou règle avec « Appliquer aussi maintenant ». */
    get appliesNow(): boolean {
        return !this.isRule || this.applyNow;
    }

    get canApply(): boolean {
        if (this.applying || this.blocked || this.selectedTagIds.length === 0) {
            return false;
        }
        if (this.isRule && this.scheduled && !this.cron) {
            return false;
        }
        return !this.appliesNow || this.personalCount > 0;
    }

    get applyLabel(): string {
        if (this.isRule && !this.applyNow) {
            return this.translate.instant('tagResults.rule.save');
        }
        const count = this.personalCount > 0 ? this.personalCount : 0;
        const prefix = this.isRule ? 'tagResults.rule.saveAndApply' : 'tagResults.apply';
        const key = `${prefix}.${this.status ? 'add' : 'remove'}.${count === 1 ? 'one' : 'other'}`;
        return this.translate.instant(key, { count: count.toLocaleString('fr-FR') });
    }

    get scopeHelpKey(): string {
        const view = this.data.personalView ? 'personal' : 'command';
        return `tagResults.scopeHelp.${view}.${this.status ? 'add' : 'remove'}`;
    }

    apply(): void {
        if (!this.canApply) {
            return;
        }
        this.applying = true;
        const scheduled = this.isRule && this.scheduled;
        this.tagJobService
            .startTagByDataview(this.data.tenant, this.data.partner, this.buildBody())
            .subscribe({
                next: job => this.ref.close({
                    status: this.status,
                    tagIds: [...this.selectedTagIds],
                    tagNames: this.tagsOptions.filter(t => this.selectedTagIds.includes(t.id)).map(t => t.name),
                    personalCount: this.personalCount,
                    job,
                    rule: this.isRule,
                    applied: this.appliesNow,
                    scheduled
                } as TagResultsDialogResult),
                // Le message d'erreur est affiché par ErrorsInterceptor ; la modale reste ouverte.
                error: () => this.applying = false
            });
    }

    cancel(): void {
        this.ref.close();
    }

    private buildBody(): UpdateTagByDataviewDTO {
        const rule = this.data.rule;
        return {
            datastore: this.data.tenant,
            idDataview: this.data.dataviewId,
            queryFilter: this.data.queryFilter,
            queryFilterUi: this.data.queryFilterUi,
            tagsIds: this.selectedTagIds,
            status: this.status,
            saveRegle: !!rule,
            affecterAndSave: !!rule && this.applyNow,
            cron: rule && this.scheduled ? this.cron : null,
            idSaveQuery: rule ? rule.presetId : null
        };
    }
}
